import { Component, Element, h, Prop, Listen, State } from '@stencil/core';
import { NjRoute } from '../nj-router/NjRoute';
import { FromToData } from '../nj-from-to/nj-from-to';
import { i18n } from '../../utils/I18n';
import {
  DATA_STORE_KEY_FROM_TO,
  DATA_STORE_KEY_FROM_TO_RETURN,
  DATA_STORE_KEY_LATEST_BOOKING_STEP,
  DATA_STORE_KEY_OUTWARD_VALUE,
  DATA_STORE_KEY_PASSENGER_EMAIL,
  DATA_STORE_KEY_RETURN_VALUE,
  dataStore
} from '../../global/DataStore';
import { journeyData, JourneyStoreEvent, NjOfferData } from "../../global/JourneyStore";
import { ReservationMode } from '../nj-reservation/ReservationMode';
import { manipulateLangNav } from '../../helpers/utils';
import { getPassangersFilteredFromStore } from '../nj-passangers/passanger-utils';
import { BookingStep } from './BookingStep';
import { RestService } from "../../services/rest.service";
import { SessionService } from "../../services/session.service";
import { scrollToElement } from "../../utils/scroll";

@Component({
  tag: 'nj-home',
  styleUrl: 'nj-home.scss',
  shadow: true,
})
export class NjHome {
  fromToSubmitLabelDefault: string = i18n.getMessage('fromTo.searchConnections', 'Verbindungen suchen');

  @Element() element: HTMLElement;

  @State() fromToMode: 'default' | 'outward' | 'return' = 'default';
  @State() fromToData: FromToData;
  @State() fromToReturnData: FromToData;
  @State() fromToSubmitLabel: string;
  @State() outwardOffer: NjOfferData | null;
  @State() returnOffer: NjOfferData | null;
  @State() isSelectionCompleted: boolean;

  @Prop() route: NjRoute;
  @State() showFormOnly: boolean;

  private scrollAfterLoad = false;

  async componentWillLoad() {
    manipulateLangNav()
    this.fromToSubmitLabel = this.fromToSubmitLabelDefault;
    // http://localhost:8080/de/booking.html/#/home?from=8011160&to=8103000

    //   console.log('showFormOnly: ', this.route.getParameter('showFormOnly', false));
    const showFormOnlyParameterValue: string = this.route.getParameter('showFormOnly', 'false').toString();
    this.showFormOnly = showFormOnlyParameterValue === 'true' ? true : false;
    const fromToData = dataStore().getSessionData(DATA_STORE_KEY_FROM_TO, null);

    const continueBookingProcess = this.route.getParameter("continue", "false") === "true" ? true : false;

    const from: string = this.route.getParameter('from', null);
    const to: string = this.route.getParameter('to', null);
    const latestBookingStep: string = dataStore().getSessionData(DATA_STORE_KEY_LATEST_BOOKING_STEP);

    if (continueBookingProcess && latestBookingStep != null && fromToData != null) {
      this.restoreStateFromFromToData(fromToData);

      const outboundData = journeyData(ReservationMode.Outward);
      const outboundConnectionSelected = outboundData.hasSelectedOffer();
      const outboundOfferSelected = dataStore().getSessionData(DATA_STORE_KEY_OUTWARD_VALUE) !== null;
      const outboundProductSelected = outboundData.hasSelectedProduct();

      const returnData = journeyData(ReservationMode.Outward);
      const returnConnectionSelected = returnData.hasSelectedOffer();
      const returnOfferSelected = dataStore().getSessionData(DATA_STORE_KEY_RETURN_VALUE) !== null;
      const returnProductSelected = returnData.hasSelectedProduct();

      const emailAddressEntered = dataStore().getSessionData(DATA_STORE_KEY_PASSENGER_EMAIL) !== null;
      // const phoneNumberEntered = dataStore().getSessionData(DATA_STORE_KEY_PHONE_NUMBER) !== null;
      const passengers = getPassangersFilteredFromStore();
      let passengerDataFilled = true;
      for (const passenger of passengers) {
        if (passenger.firstName == null || passenger.lastName == null || passenger.gender == null || passenger.birthDate == null) {
          passengerDataFilled = false;
        }
      }
      // console.log(`outboundConnectionSelected: ${outboundConnectionSelected}, outboundOfferSelected: ${outboundOfferSelected}, returnConnectionSelected: ${returnConnectionSelected}, returnOfferSelected: ${returnOfferSelected}, emailAddressEntered: ${emailAddressEntered}, passengerDataFilled: ${passengerDataFilled}`);

      switch (latestBookingStep) {
        case BookingStep.FROM_TO:
          location.href = '#/home';
          break;
        case BookingStep.RESERVATION:
          if (outboundConnectionSelected && this.fromToData.oneWay) {
            location.href = '#/reservierung';
          } else if (outboundConnectionSelected && !this.fromToData.oneWay && returnConnectionSelected) {
            location.href = '#/reservierung';
          }
          break;
        case BookingStep.PASSENGERS:
          if (outboundConnectionSelected && this.fromToData.oneWay && outboundOfferSelected) {
            location.href = "#/reisende";
          } else if (outboundConnectionSelected && !this.fromToData.oneWay && returnConnectionSelected && outboundOfferSelected && returnOfferSelected) {
            location.href = "#/reisende";
          }
          break;
        case BookingStep.SUMMARY:
          if (outboundConnectionSelected && this.fromToData.oneWay && outboundOfferSelected && emailAddressEntered && passengerDataFilled) {
            if (outboundProductSelected) {
              location.href = "#/zusammenfassung";
            } else {
              location.href = "#/reisende";
            }
          } else if (outboundConnectionSelected && !this.fromToData.oneWay && returnConnectionSelected && outboundOfferSelected && returnOfferSelected && emailAddressEntered && passengerDataFilled) {
            if (outboundProductSelected && returnProductSelected) {
              location.href = "#/zusammenfassung";
            } else {
              location.href = "#/reisende";
            }
          }
          break;
        default:
          console.error(`The booking step "${latestBookingStep} corresponds to an unknown value."`);
      }
    } else if (from && to) {
      const [fromObject, toObject] = await Promise.all([
        this.loadStationInfo(from),
        this.loadStationInfo(to),
      ]);

      const dateString: string = this.route.getParameter('date', null);
      const outwardDate = dateString ? new Date(dateString + "T12:00:00.000Z") : new Date();
      const returnDate = new Date(outwardDate.getTime() + (1000 * 60 * 60 * 24));

      this.fromToData = {
        mode: 'default',
        from: fromObject,
        to: toObject,
        dateOutward: { value: '', context: outwardDate },
        dateReturn: { value: '', context: returnDate },
      };
      this.fromToMode = 'default';
      dataStore().setSessionData(DATA_STORE_KEY_FROM_TO, this.fromToData);
      dataStore().removeSessionData(DATA_STORE_KEY_FROM_TO_RETURN);

      if (fromObject && toObject) {
        setTimeout(() => {
          const form: HTMLNjFromToElement = this.element.shadowRoot.querySelector('nj-from-to');
          form?.submitForm()
        }, 50);
      }
    } else if (fromToData != null) {
      this.scrollAfterLoad = !journeyData(ReservationMode.Outward).hasSelectedOffer();
      this.restoreStateFromFromToData(fromToData);
    }

    // Initialize with existing selection
    this.outwardOffer = journeyData(ReservationMode.Outward).getNjOffer();
    this.returnOffer = journeyData(ReservationMode.Return).getNjOffer();
  }

  componentDidLoad() {
    if (this.scrollAfterLoad) {
      this.scrollTo('.js-nj-outward-journey', 250);
    }
  }

  private async loadStationInfo(eva: string) {
    try {
      const data = await RestService.instance.getStation(eva);
      return data ? { context: data, value: data.name } : null;
    } catch (e) {
      console.error(`Station search for ${eva} failed:`, e);
      return null;
    }
  }

  private restoreStateFromFromToData(fromToData: FromToData) {
    const fromToReturnData = dataStore().getSessionData(DATA_STORE_KEY_FROM_TO_RETURN);
    if (fromToReturnData) {
      // This means that the return form is visible
      this.fromToMode = 'outward';
      this.fromToSubmitLabel = i18n.getMessage('fromTo.searchConnectionsOutward', 'Hinfahrt suchen');
    }

    this.fromToData = fromToData;
    this.fromToReturnData = fromToReturnData;
  }

  @Listen('njFromToFormSubmitted')
  njFromToFormSubmittedHandler(e: CustomEvent<FromToData>) {
    const fromToData = { ...e.detail };

    // This means that the return form is visible
    if (!fromToData.dateReturn && this.fromToData && this.fromToData.dateReturn && this.fromToMode == 'outward') {
      fromToData.dateReturn = { ...this.fromToData.dateReturn };
    }

    switch (fromToData.mode) {
      case "default":
        this.outwardOffer = null;
        this.returnOffer = null;
        journeyData(ReservationMode.Outward).clearAll();
        journeyData(ReservationMode.Return).clearAll();
        dataStore().setSessionData(DATA_STORE_KEY_FROM_TO, fromToData);
        break;
      case "outward":
        this.outwardOffer = null;
        journeyData(ReservationMode.Outward).clearAll();
        dataStore().setSessionData(DATA_STORE_KEY_FROM_TO, fromToData);
        break;
      case "return":
        this.returnOffer = null;
        journeyData(ReservationMode.Return).clearAll();
        dataStore().setSessionData(DATA_STORE_KEY_FROM_TO_RETURN, fromToData);
        break;
    }

    this.isSelectionCompleted = null;
    if (this.showFormOnly) {
      if (fromToData.manualSubmit) {
        location.href = SessionService.instance.getBookingUrl();
      }
    } else {
      if (fromToData.mode !== 'return') {
        this.fromToData = fromToData;
        this.scrollTo('.js-nj-outward-journey', 250);
      } else {
        this.fromToReturnData = fromToData;
        this.scrollTo('.js-nj-return-journey nj-connection');
      }
    }
  }

  /*
  @Listen('njFromToFormNotComplited')
  njFromToFormNotComplitedHandler(e: CustomEvent<FromToData>) {

  }
  */
  scrollTo(selector: string, delay = 0) {
    const element = this.element.shadowRoot.querySelector(selector) as HTMLElement;
    if (element) {
      scrollToElement(element, delay);
      element.focus();
    }
  }

  @Listen('scrollToDirection')
  scrollToPart(e: CustomEvent){
    this.scrollTo(`.js-nj-${e.detail}-journey`, 250)
  }

  @Listen('njReturnFormActivated')
  njReturnFormActivatedHandler() {
    this.fromToMode = 'outward';
    this.fromToSubmitLabel = i18n.getMessage('fromTo.searchConnectionsOutward', 'Hinfahrt suchen');
  }

  @Listen('njReturnFormDeactivated')
  njReturnFormDeactivatedHandler() {
    dataStore().removeSessionData(DATA_STORE_KEY_FROM_TO_RETURN)
    dataStore().removeSessionData(ReservationMode.Return)
    dataStore().removeSessionData(DATA_STORE_KEY_RETURN_VALUE)
    this.returnOffer = null

    // The regular form allows return connections again, but leave it at "one way" until selected manually
    this.fromToMode = 'default';
    this.fromToData.dateReturn = null;
    this.fromToData.oneWay = true;
    this.fromToSubmitLabel = this.fromToSubmitLabelDefault;
    dataStore().setSessionData(DATA_STORE_KEY_FROM_TO, this.fromToData);
  }

  @Listen('njOfferSearchDone')
  njOfferSearchDoneHandler(e: CustomEvent<{ mode: ReservationMode }>) {
    // Unset the selected offer whenever new offers have been loaded
    switch (e.detail.mode) {
      case ReservationMode.Outward:
        this.outwardOffer = null;
        journeyData(ReservationMode.Outward).clearAll();
        break;
      case ReservationMode.Return:
        this.returnOffer = null;
        journeyData(ReservationMode.Return).clearAll();
        break;
    }
  }

  @Listen('njConnectionSelected', { target: 'window' })
  njConnectionSelectedHandler(e: JourneyStoreEvent) {
    const offer = journeyData(e.detail.reservationMode).getNjOffer();
    switch (e.detail.reservationMode) {
      case ReservationMode.Outward:
        this.outwardOffer = offer;
        break;
      case ReservationMode.Return:
        this.returnOffer = offer;
        break;
    }

    if (offer) {
      this.scrollToAfterSelection();
    }
  }

  scrollToAfterSelection() {
    if (this.fromToData.dateOutward && this.fromToData.dateReturn) {
      if (this.outwardOffer && this.returnOffer) {
        this.scrollTo('nj-selection-summary', 250);
        this.animateReservationBttn(3250);
      } else if (!this.outwardOffer) {
        this.scrollTo('.js-nj-outward-journey');
      } else if (!this.returnOffer) {
        this.scrollTo('.js-nj-return-journey', 250);
      }
    } else {
      this.scrollTo('nj-selection-summary', 250);
      this.animateReservationBttn(3250);
    }
  }

  animateReservationBttn(delay: number = 0) {
    setTimeout(() => {
      this.element.shadowRoot.querySelector('nj-selection-summary').classList.add('animate');
      setTimeout(() => {
        this.element.shadowRoot.querySelector('nj-selection-summary').classList.remove('animate');
      }, 4000);
    }, delay);
  }

  render() {
    return (
      <div>
        {this.showFormOnly ? '' : <nj-breadcrumb menuIndex={0} />}
        <nj-from-to mode={this.fromToMode} showFormOnly={true} submitLabel={this.fromToSubmitLabel} />
        {this.showFormOnly ? (
          ''
        ) : (
          <div>
            <nj-connections fromToData={this.fromToData} fromToReturnData={this.fromToReturnData} parentShadowRoot={this.element.shadowRoot} />
            {this.fromToData && <nj-selection-summary fromToData={this.fromToData} isSelectionCompleted={this.isSelectionCompleted} outwardOffer={this.outwardOffer} returnOffer={this.returnOffer} />}
          </div>
        )}
      </div>
    );
  }
}
